import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Calculator,
  Trash2,
  Plus,
  Layers,
  Sparkles,
  TrendingUp,
  PiggyBank,
  Save,
  CalendarDays,
} from 'lucide-react';
import { useFavoriteBets } from '@/contexts/FavoriteBetsContext';
import { isValidDecimalOdd } from '@/utils/favoriteBet';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import GestaoSavedMultiplesPanel from '@/components/gestao/GestaoSavedMultiplesPanel';
import { commitGestaoSnapshot } from '@/components/gestao/GestaoSaveSnapshotSection';
import { useSavedMultiples } from '@/hooks/useSavedMultiples';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { parseReferenceDateKey } from '@/utils/savedMultiples';

function parseStake(raw) {
  const n = parseFloat(String(raw).replace(',', '.'));
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function sourceBadge(source) {
  if (source === 'suggestion')
    return (
      <Badge
        variant="outline"
        className="text-[9px] font-semibold uppercase tracking-wide border-sky-200/80 dark:border-sky-700/50 bg-sky-50/90 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300"
      >
        Sugestão
      </Badge>
    );
  if (source === 'prediction')
    return (
      <Badge
        variant="outline"
        className="text-[9px] font-semibold uppercase tracking-wide border-violet-200/80 dark:border-violet-800/50 bg-violet-50/90 dark:bg-violet-950/40 text-violet-800 dark:text-violet-300"
      >
        Palpite
      </Badge>
    );
  if (source === 'manual')
    return (
      <Badge
        variant="outline"
        className="text-[9px] font-semibold uppercase tracking-wide border-zinc-200/80 dark:border-zinc-600/50 bg-zinc-100/90 dark:bg-zinc-900/50 text-zinc-700 dark:text-zinc-300"
      >
        Manual
      </Badge>
    );
  return (
    <Badge variant="outline" className="text-[9px] font-semibold uppercase tracking-wide">
      {source}
    </Badge>
  );
}

/**
 * @param {{ embedMode?: 'page' | 'dock' }} props
 */
export default function GestaoWorkspace({ embedMode = 'page' }) {
  const navigate = useNavigate();
  const isDock = embedMode === 'dock';
  const { snapshots, addSnapshot, removeSnapshot, updateSnapshot } = useSavedMultiples();
  const { favorites, addFavorite, removeFavorite, updateFavorite, clearFavorites } =
    useFavoriteBets();
  const [stakeStr, setStakeStr] = useState('10');
  const [manualLabel, setManualLabel] = useState('');
  const [manualOdd, setManualOdd] = useState('');
  const [referenceDate, setReferenceDate] = useState(() => new Date());
  const [saveTitle, setSaveTitle] = useState('');

  const stake = parseStake(stakeStr);

  const snapshotDatesWithRecords = useMemo(() => {
    const set = new Set(snapshots.map((s) => s.referenceDate));
    return Array.from(set)
      .map(parseReferenceDateKey)
      .filter(Boolean);
  }, [snapshots]);

  const legs = useMemo(() => {
    return favorites.map((f) => {
      const odd =
        f.decimalOdd != null && Number.isFinite(Number(f.decimalOdd))
          ? Number(f.decimalOdd)
          : null;
      return { ...f, resolvedOdd: odd, oddValid: odd != null && isValidDecimalOdd(odd) };
    });
  }, [favorites]);

  const combinedOdd = useMemo(() => {
    const valid = legs.filter((l) => l.oddValid);
    if (valid.length === 0) return null;
    const p = valid.reduce((acc, l) => acc * l.resolvedOdd, 1);
    return Math.round(p * 10000) / 10000;
  }, [legs]);

  const grossReturn = combinedOdd != null ? Math.round(stake * combinedOdd * 100) / 100 : null;
  const estimatedProfit =
    grossReturn != null ? Math.round((grossReturn - stake) * 100) / 100 : null;

  const missingOddCount = legs.filter((l) => !l.oddValid).length;

  const handleAddManual = () => {
    const label = manualLabel.trim();
    const odd = parseFloat(String(manualOdd).replace(',', '.'));
    if (!label || !isValidDecimalOdd(odd)) return;
    addFavorite({
      id: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      label,
      decimalOdd: odd,
      source: 'manual',
      createdAt: new Date().toISOString(),
    });
    setManualLabel('');
    setManualOdd('');
  };

  return (
    <div className={cn(isDock ? 'space-y-4' : 'space-y-8')}>
      {!isDock && (
        <header className="mb-10 space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-emerald-200/60 dark:border-emerald-500/20 bg-emerald-500/10 dark:bg-emerald-500/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-800 dark:text-emerald-300">
            <Sparkles className="h-3.5 w-3.5" />
            Simulador de múltipla
          </div>
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex min-w-0 flex-1 items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 shadow-lg shadow-emerald-500/25 ring-1 ring-white/20">
                <Calculator className="h-7 w-7 text-white" />
              </div>
              <div className="min-w-0 space-y-2">
                <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white sm:text-4xl">
                  Gestão{' '}
                  <span className="bg-gradient-to-r from-emerald-500 to-teal-500 dark:from-emerald-400 dark:to-teal-400 bg-clip-text text-transparent">
                    de múltipla
                  </span>
                </h1>
                <p className="max-w-xl text-sm leading-relaxed text-slate-600 dark:text-neutral-400">
                  Seleções, cálculo e salvar registro no mesmo painel. Ajuste odds quando precisar.
                </p>
              </div>
            </div>
            {legs.length > 0 && (
              <span className="rounded-full bg-slate-900 px-3 py-1.5 text-sm font-bold tabular-nums text-white dark:bg-white dark:text-slate-900">
                {legs.length} {legs.length === 1 ? 'seleção' : 'seleções'}
              </span>
            )}
          </div>
        </header>
      )}

      <Card
        className={cn(
          'relative overflow-hidden border border-slate-200/70 dark:border-white/[0.08] bg-white/75 dark:bg-neutral-900/50 backdrop-blur-xl shadow-[0_4px_24px_-4px_rgba(15,23,42,0.08)] dark:shadow-[0_4px_40px_-8px_rgba(0,0,0,0.55)]',
          isDock ? 'rounded-2xl' : 'rounded-3xl',
        )}
      >
        <div className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-px bg-gradient-to-r from-transparent via-emerald-400/40 to-transparent dark:via-emerald-500/30" />
        <CardHeader className="space-y-0 border-b border-slate-200/60 pb-3 pt-4 dark:border-white/[0.06]">
          <div className="flex w-full min-w-0 items-center justify-between gap-2">
            <CardTitle className="mb-0 flex min-w-0 items-center gap-2 text-lg font-bold text-slate-900 dark:text-white">
              <Calculator className="h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
              Múltipla
            </CardTitle>
            <Button
              type="button"
              size="sm"
              onClick={() =>
                commitGestaoSnapshot({
                  legs,
                  stake,
                  combinedOdd,
                  grossReturn,
                  estimatedProfit,
                  addSnapshot,
                  referenceDate,
                  saveTitle,
                  onSuccess: () => setSaveTitle(''),
                })
              }
              disabled={legs.length === 0}
              className="h-9 shrink-0 gap-1 rounded-xl bg-emerald-600 px-3 font-semibold text-white shadow-md shadow-emerald-600/20 hover:bg-emerald-700 disabled:opacity-50"
            >
              <Save className="h-3.5 w-3.5" />
              Salvar
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-5 pt-4 pb-4">
          <div>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-neutral-400">
                <Layers className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                Seleções
              </p>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 shrink-0 rounded-lg border-slate-200/90 bg-white px-2.5 text-[11px] font-semibold tabular-nums dark:border-white/[0.08] dark:bg-neutral-900/60"
                  >
                    <CalendarDays className="mr-1.5 h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                    {format(referenceDate, 'dd/MM/yyyy', { locale: ptBR })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-2" align="end">
                  <Calendar
                    mode="single"
                    selected={referenceDate}
                    onSelect={(d) => d && setReferenceDate(d)}
                    locale={ptBR}
                    weekStartsOn={0}
                    modifiers={{ temRegisto: snapshotDatesWithRecords }}
                    modifiersClassNames={{
                      temRegisto:
                        'relative after:absolute after:bottom-0.5 after:left-1/2 after:h-1 after:w-1 after:-translate-x-1/2 after:rounded-full after:bg-emerald-500',
                    }}
                    className="rounded-xl border-0 bg-transparent p-0 dark:bg-transparent"
                  />
                </PopoverContent>
              </Popover>
            </div>
            {legs.length === 0 ? (
              <div
                className={cn(
                  'rounded-2xl border border-dashed border-slate-200/90 bg-slate-50/50 text-center dark:border-white/[0.08] dark:bg-neutral-950/30',
                  isDock ? 'px-4 py-8' : 'px-8 py-14',
                )}
              >
                <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 dark:bg-neutral-800">
                  <Layers className="h-7 w-7 text-slate-400 dark:text-neutral-500" />
                </div>
                <p className="mb-1 text-base font-semibold text-slate-800 dark:text-gray-100">
                  Nenhuma seleção na múltipla
                </p>
                <p className="mb-6 text-sm text-slate-500 dark:text-neutral-400">
                  Comece por favoritar sugestões ou palpites nas outras páginas.
                </p>
                <Button
                  onClick={() => navigate('/home')}
                  className="rounded-xl bg-emerald-600 font-semibold text-white shadow-md shadow-emerald-600/20 hover:bg-emerald-700"
                >
                  Ir ao início
                </Button>
              </div>
            ) : (
              <ul className="space-y-2.5">
                {legs.map((leg) => (
                  <li
                    key={leg.id}
                    className="flex flex-col gap-3 rounded-2xl border border-slate-200/80 bg-white/80 p-4 transition-all dark:border-white/[0.07] dark:bg-neutral-950/40 sm:flex-row sm:items-center sm:gap-4"
                  >
                    <div className="min-w-0 flex-1 space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        {sourceBadge(leg.source)}
                        {!leg.oddValid && (
                          <span className="text-[10px] font-medium uppercase tracking-wide text-amber-700 dark:text-amber-400/90">
                            Odd pendente
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-medium leading-snug text-slate-900 dark:text-gray-100">
                        {leg.label}
                      </p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2 sm:gap-3">
                      <div className="flex items-center gap-2 rounded-xl border border-slate-200/80 bg-slate-50/90 px-2.5 py-1.5 dark:border-white/[0.08] dark:bg-neutral-900/60">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 dark:text-neutral-500">
                          Odd
                        </span>
                        <Input
                          type="text"
                          inputMode="decimal"
                          className="h-8 w-[4.5rem] border-0 bg-transparent p-0 text-right text-sm font-bold tabular-nums text-slate-900 shadow-none focus-visible:ring-0 dark:text-white"
                          value={leg.decimalOdd != null ? String(leg.decimalOdd) : ''}
                          placeholder="—"
                          onChange={(e) => {
                            const v = e.target.value.replace(',', '.');
                            if (v === '') {
                              updateFavorite(leg.id, { decimalOdd: null });
                              return;
                            }
                            const n = parseFloat(v);
                            updateFavorite(leg.id, {
                              decimalOdd: Number.isFinite(n) ? n : null,
                            });
                          }}
                        />
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-10 w-10 rounded-xl text-slate-400 transition-colors hover:bg-red-500/10 hover:text-red-600 dark:hover:text-red-400"
                        aria-label="Remover seleção"
                        onClick={() => removeFavorite(leg.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {legs.length > 0 && (
              <div className="flex justify-end border-t border-slate-200/60 pt-4 dark:border-white/[0.06]">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-slate-600 hover:bg-red-500/10 hover:text-red-700 dark:text-neutral-400 dark:hover:text-red-400"
                  onClick={clearFavorites}
                >
                  Limpar tudo
                </Button>
              </div>
            )}

            <div className="rounded-2xl border border-slate-200/70 bg-slate-50/80 p-4 dark:border-white/[0.06] dark:bg-neutral-950/35">
              <p className="mb-3 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-neutral-400">
                <Plus className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                Adicionar manual
              </p>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  placeholder="Descrição (ex. Flamengo x Palmeiras — Over 2.5)"
                  value={manualLabel}
                  onChange={(e) => setManualLabel(e.target.value)}
                  className="h-11 flex-1 rounded-xl border-slate-200/90 bg-white dark:border-white/[0.08] dark:bg-neutral-900/60"
                />
                <div className="flex gap-2">
                  <Input
                    type="text"
                    inputMode="decimal"
                    placeholder="Odd"
                    className="h-11 w-28 rounded-xl tabular-nums border-slate-200/90 bg-white dark:border-white/[0.08] dark:bg-neutral-900/60"
                    value={manualOdd}
                    onChange={(e) => setManualOdd(e.target.value)}
                  />
                  <Button
                    type="button"
                    onClick={handleAddManual}
                    className="h-11 shrink-0 rounded-xl bg-emerald-600 px-4 font-semibold text-white shadow-md shadow-emerald-600/20 hover:bg-emerald-700"
                  >
                    <Plus className="mr-1.5 h-4 w-4" />
                    Adicionar
                  </Button>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-slate-200/60 pt-6 dark:border-white/[0.06]">
            <p className="mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-neutral-400">
              <TrendingUp className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
              Cálculo
            </p>
            <div className="mb-3" />
            <div>
              <label className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-neutral-400">
                <PiggyBank className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />
                Valor apostado (R$)
              </label>
              <Input
                type="text"
                inputMode="decimal"
                value={stakeStr}
                onChange={(e) => setStakeStr(e.target.value)}
                className="h-11 max-w-xs rounded-xl tabular-nums border-slate-200/90 bg-white font-semibold dark:border-white/[0.08] dark:bg-neutral-950/50"
              />
            </div>

            <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div
                className={cn(
                  'relative overflow-hidden rounded-2xl border p-5 transition-shadow',
                  combinedOdd != null
                    ? 'border-emerald-300/60 bg-gradient-to-br from-emerald-50/90 to-white shadow-[0_0_24px_-8px_rgba(16,185,129,0.35)] dark:border-emerald-800/40 dark:from-emerald-950/50 dark:to-neutral-950/80 dark:shadow-[0_0_32px_-10px_rgba(16,185,129,0.2)]'
                    : 'border-slate-200/80 bg-slate-50/90 dark:border-white/[0.07] dark:bg-neutral-950/40',
                )}
              >
                {combinedOdd != null && (
                  <div className="pointer-events-none absolute -right-6 -top-6 h-24 w-24 rounded-full bg-emerald-400/20 blur-2xl dark:bg-emerald-500/15" />
                )}
                <p className="relative text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-neutral-500">
                  Odd final
                </p>
                <p className="relative mt-1 text-2xl font-black tabular-nums tracking-tight text-slate-900 dark:text-white">
                  {combinedOdd != null ? combinedOdd.toFixed(2) : '—'}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 dark:border-white/[0.07] dark:bg-neutral-950/40">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-neutral-500">
                  Retorno estimado
                </p>
                <p className="mt-1 text-2xl font-black tabular-nums tracking-tight text-emerald-600 dark:text-emerald-400">
                  {grossReturn != null
                    ? `R$ ${grossReturn.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'}
                </p>
              </div>
              <div className="rounded-2xl border border-slate-200/80 bg-white/90 p-5 dark:border-white/[0.07] dark:bg-neutral-950/40">
                <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-neutral-500">
                  Lucro líquido (est.)
                </p>
                <p
                  className={cn(
                    'mt-1 text-2xl font-black tabular-nums tracking-tight',
                    estimatedProfit != null && estimatedProfit >= 0
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-rose-600 dark:text-rose-400',
                  )}
                >
                  {estimatedProfit != null
                    ? `R$ ${estimatedProfit.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : '—'}
                </p>
              </div>
            </div>

            {missingOddCount > 0 && (
              <p className="mt-4 rounded-xl border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 text-xs font-medium text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
                Preencha a odd em {missingOddCount} seleção(ões) para calcular a múltipla.
              </p>
            )}
          </div>

          <p className="text-[11px] leading-relaxed text-slate-500 dark:text-neutral-500">
            Odds reais variam por casa e momento. Jogue com responsabilidade. 18+
          </p>
        </CardContent>
      </Card>

      {!isDock && (
        <div>
          <GestaoSavedMultiplesPanel
            snapshots={snapshots}
            removeSnapshot={removeSnapshot}
            updateSnapshot={updateSnapshot}
          />
        </div>
      )}
    </div>
  );
}
